
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { isAuthenticated, isAdmin, isFullAdmin, logoutUser, getCurrentUser } from '@/utils/auth';
import { safeUserRole } from '@/utils/roleUtils';
import { getBusinessSettings, BusinessSettings } from '@/utils/businessService';
import AdminLayout from '@/components/admin/AdminLayout';
import BusinessSettingsTabs from '@/components/business/BusinessSettingsTabs';

const AdminBusinessSetup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('faq');
  const [settings, setSettings] = useState<BusinessSettings | null>(null);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    // Get current user
    const currentUser = getCurrentUser();
    const userRole = currentUser?.role || '';
    
    setUser(currentUser);
    
    if (!isAuthenticated() || !isAdmin(safeUserRole(userRole))) {
      navigate('/login');
      return;
    }
    
    // Check if user is a full admin
    if (!isFullAdmin(safeUserRole(userRole))) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this area",
        variant: "destructive",
      });
      navigate('/admin/dashboard');
      return;
    }

    // Load business settings
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        const businessSettings = await getBusinessSettings();
        setSettings(businessSettings);
        
        // If domain is in the URL, set the active tab to domain
        if (window.location.search.includes('tab=domain')) {
          setActiveTab('domain');
        }
      } catch (error) {
        console.error('Error loading business settings:', error);
        toast({
          title: "Error",
          description: "Failed to load business settings",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [navigate, toast]);

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleSettingsSaved = async () => {
    // Reload settings after saving
    try {
      const businessSettings = await getBusinessSettings();
      setSettings(businessSettings);
      toast({
        title: "Success",
        description: "Settings saved successfully",
      });
    } catch (error) {
      console.error('Error reloading settings:', error);
    }
  };

  return (
    <AdminLayout 
      isLoading={isLoading}
      userRole={user?.role}
      onLogout={handleLogout}
      activeTab="business-setup"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <BusinessSettingsTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          settings={settings}
          onSettingsSaved={handleSettingsSaved}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminBusinessSetup;
